import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { CalendarIcon } from 'lucide-react';
import { format, differenceInMonths } from 'date-fns';
import { useFormatting } from '@/hooks/useFormatting';

interface AddRecurringExpenseModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAdd: (adjustment: any) => void;
}

const EXPENSE_CATEGORIES = ['Salaries', 'Rent', 'Software', 'Utilities', 'Marketing', 'Other'];
const FREQUENCIES = ['Weekly', 'Monthly', 'Quarterly', 'Annually'];

export function AddRecurringExpenseModal({ open, onOpenChange, onAdd }: AddRecurringExpenseModalProps) {
  const { formatCurrency } = useFormatting();
  const [description, setDescription] = useState('');
  const [amount, setAmount] = useState('');
  const [frequency, setFrequency] = useState('Monthly');
  const [startDate, setStartDate] = useState<Date>();
  const [endDate, setEndDate] = useState<Date>();
  const [category, setCategory] = useState('');

  const amountNum = parseFloat(amount) || 0;
  const isValid = description && amountNum > 0 && frequency && startDate && category;

  const monthlyAmount = frequency === 'Weekly' ? amountNum * 4.33 :
                        frequency === 'Monthly' ? amountNum :
                        frequency === 'Quarterly' ? amountNum / 3 :
                        amountNum / 12;

  const totalMonths = startDate && endDate ? differenceInMonths(endDate, startDate) : 12;
  const totalCost = endDate ? monthlyAmount * totalMonths : monthlyAmount * 12;

  useEffect(() => {
    if (!open) {
      setDescription('');
      setAmount('');
      setFrequency('Monthly');
      setStartDate(undefined);
      setEndDate(undefined);
      setCategory('');
    }
  }, [open]);

  const handleAdd = () => {
    if (!isValid) return;

    onAdd({
      type: 'recurring-expense',
      description: description,
      amount: -amountNum,
      frequency: frequency,
      startDate: startDate,
      endDate: endDate,
      category: category,
      impact: endDate 
        ? `Recurring expense of £${(monthlyAmount / 1000).toFixed(1)}K/month for ${totalMonths} months (Total: £${(totalCost / 1000).toFixed(1)}K)`
        : `Recurring expense of £${(monthlyAmount / 1000).toFixed(1)}K/month (ongoing)`,
    });

    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Add Recurring Expense</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Description */}
          <div className="space-y-2">
            <Label>Description *</Label>
            <Textarea
              placeholder="e.g., New software subscription"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
            />
          </div>

          {/* Amount */}
          <div className="space-y-2">
            <Label>Amount (£) per period *</Label>
            <Input
              type="number"
              placeholder="0.00"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              min="0"
              step="0.01"
            />
          </div>

          {/* Frequency */}
          <div className="space-y-2">
            <Label>Frequency *</Label>
            <Select value={frequency} onValueChange={setFrequency}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {FREQUENCIES.map((freq) => (
                  <SelectItem key={freq} value={freq}>
                    {freq}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Start Date */}
          <div className="space-y-2">
            <Label>Start Date *</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="w-full justify-start text-left font-normal">
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {startDate ? format(startDate, 'MMM dd, yyyy') : 'Pick a date'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0">
                <Calendar
                  mode="single"
                  selected={startDate}
                  onSelect={setStartDate}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          </div>

          {/* End Date */}
          <div className="space-y-2">
            <Label>End Date (optional - leave blank for ongoing)</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="w-full justify-start text-left font-normal">
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {endDate ? format(endDate, 'MMM dd, yyyy') : 'Ongoing'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0">
                <Calendar
                  mode="single"
                  selected={endDate}
                  onSelect={setEndDate}
                  disabled={(date) => startDate ? date < startDate : false}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
            {endDate && (
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => setEndDate(undefined)}
                className="text-xs"
              >
                Clear end date
              </Button>
            )}
          </div>

          {/* Category */}
          <div className="space-y-2">
            <Label>Category *</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger>
                <SelectValue placeholder="Select category..." />
              </SelectTrigger>
              <SelectContent>
                {EXPENSE_CATEGORIES.map((cat) => (
                  <SelectItem key={cat} value={cat}>
                    {cat}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Impact Preview */}
          {isValid && (
            <div className="border-t pt-4 space-y-2">
              <p className="text-sm font-medium">Impact Preview</p>
              <div className="space-y-1 text-sm">
                <div className="flex justify-between">
                  <span>Monthly cost:</span>
                  <span className="text-red-500">-{formatCurrency(monthlyAmount, 'full', { maximumFractionDigits: 0 })}</span>
                </div>
                {endDate && (
                  <>
                    <div className="flex justify-between">
                      <span>Duration:</span>
                      <span>{totalMonths} months</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Total cost:</span>
                      <span className="text-red-500">-{formatCurrency(totalCost, 'full', { maximumFractionDigits: 0 })}</span>
                    </div>
                  </>
                )}
                <div className="pt-2 border-t">
                  <p className="text-muted-foreground">
                    {endDate 
                      ? `Net effect: £${(monthlyAmount / 1000).toFixed(1)}K/month for ${totalMonths} months`
                      : `Net effect: £${(monthlyAmount / 1000).toFixed(1)}K/month (ongoing)`
                    }
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-4">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button onClick={handleAdd} disabled={!isValid}>
              Add Adjustment
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
